/**
 * VersionStepper — minimal "↺ N earlier wordings" tray that opens to reveal
 * snapshots of prior generations. Mounted under the PackIterationStrip on
 * CardDetail.
 *
 * iter-72 (d): solves the "iterated past my favorite wording" regret. Each
 * regenerate snapshots the prior cards into `mood.versions[]`. Users tap an
 * entry here to revert (one API call). The revert is itself reversible —
 * backend pushes the previously-current state onto versions, so users can
 * step forward again.
 *
 * Renders the relatable variant's body as the preview snippet (concise).
 * Free + Premium both get this — it's a comfort feature, not a paywall.
 */
import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { History, Check, RotateCcw } from "lucide-react";
import { revertMoodCard } from "../lib/api";
import { track } from "../lib/track";

function pickRelatableSnippet(cards) {
    if (!Array.isArray(cards) || cards.length === 0) return "";
    const r = cards.find((c) => c?.style === "relatable") || cards[0];
    const body = (r?.body || "").trim();
    if (!body) return "";
    // Pull first line — keeps snippet tight in the row.
    const firstLine = body.split("\n")[0] || body;
    return firstLine.length > 80 ? firstLine.slice(0, 78) + "…" : firstLine;
}

function formatToneChip(tone) {
    if (!tone) return "auto";
    return tone;
}

export default function VersionStepper({ moodId, versions, onReverted }) {
    const [open, setOpen] = useState(false);
    const [busyIdx, setBusyIdx] = useState(null);

    const list = Array.isArray(versions) ? versions : [];
    if (list.length === 0) return null;

    const handleRevert = async (idx) => {
        if (busyIdx !== null) return;
        setBusyIdx(idx);
        try {
            track("version_revert", { mood_id: moodId, index: idx });
            const updated = await revertMoodCard(moodId, idx);
            if (onReverted) onReverted(updated);
            setOpen(false);
        } catch (e) {
            // silent failure — UI just stays put; toast handling lives in parent
            console.warn("revert failed", e);
        } finally {
            setBusyIdx(null);
        }
    };

    // Reversed display order — newest snapshot first (top), oldest last.
    const display = list.slice().reverse().map((v, i) => ({
        ...v,
        // Translate display index back to backend index
        backendIndex: list.length - 1 - i,
    }));

    return (
        <div className="mt-3" data-testid="version-stepper-root">
            <button
                type="button"
                onClick={() => {
                    setOpen((s) => !s);
                    if (!open) track("version_stepper_open", { mood_id: moodId, count: list.length });
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/12 bg-white/4 hover:bg-white/8 text-white/70 hover:text-white/95 transition-colors text-[11px] tracking-[0.12em] uppercase font-medium"
                data-testid="version-stepper-toggle"
                aria-expanded={open}
            >
                <History className="w-3.5 h-3.5" />
                <span>
                    {list.length} earlier wording{list.length === 1 ? "" : "s"}
                </span>
                <span className={`text-[10px] opacity-60 transition-transform ${open ? "rotate-180" : ""}`}>▾</span>
            </button>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: -6, height: 0 }}
                        animate={{ opacity: 1, y: 0, height: "auto" }}
                        exit={{ opacity: 0, y: -6, height: 0 }}
                        transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                        className="overflow-hidden"
                    >
                        <ul className="mt-3 space-y-2" data-testid="version-stepper-list">
                            {display.map((v) => {
                                const snippet = pickRelatableSnippet(v.cards);
                                return (
                                    <li key={`${v.ts}-${v.backendIndex}`}>
                                        <button
                                            type="button"
                                            disabled={busyIdx !== null}
                                            onClick={() => handleRevert(v.backendIndex)}
                                            className="group w-full text-left rounded-xl border border-white/8 bg-white/3 hover:border-amber-300/30 hover:bg-amber-500/5 px-3 py-2.5 transition-colors disabled:opacity-40 disabled:cursor-wait"
                                            data-testid={`version-stepper-item-${v.backendIndex}`}
                                        >
                                            <div className="flex items-start gap-3">
                                                <div className="shrink-0 mt-0.5">
                                                    {busyIdx === v.backendIndex ? (
                                                        <motion.div
                                                            animate={{ rotate: 360 }}
                                                            transition={{ duration: 0.8, repeat: Infinity, ease: "linear" }}
                                                        >
                                                            <RotateCcw className="w-3.5 h-3.5 text-amber-300" />
                                                        </motion.div>
                                                    ) : (
                                                        <RotateCcw className="w-3.5 h-3.5 text-white/45 group-hover:text-amber-300 transition-colors" />
                                                    )}
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex items-center gap-2 text-[10px] tracking-[0.18em] uppercase text-white/40 mb-1">
                                                        <span className="text-amber-200/70 normal-case tracking-normal italic font-serif">
                                                            {formatToneChip(v.tone)}
                                                        </span>
                                                        <span>·</span>
                                                        <span>tap to revert</span>
                                                    </div>
                                                    <div className="text-sm text-white/85 font-serif leading-snug line-clamp-2">
                                                        {snippet || <span className="text-white/40 italic">(empty version)</span>}
                                                    </div>
                                                </div>
                                                {busyIdx === v.backendIndex && (
                                                    <Check className="w-3.5 h-3.5 text-emerald-300 shrink-0 mt-0.5" />
                                                )}
                                            </div>
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                        <p className="mt-2 text-[10px] tracking-[0.16em] uppercase text-white/35 text-center">
                            reverts don't cost a regenerate
                        </p>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
